<template>
  <nav class="sidebar bg-black">
    <div
      class="sidebar-items"
      :class="{ 'sidebar-items--hidden': isHidden }"
    >
      <sidebarItem
        v-for="item in staticArray"
        :key="item.id"
        :item="item"
        :class="{
          'sidebar-item--active': panelId === item.id || (!panelId && item.active) || (item.id === 'home' && $route.name === 'home'),
          'sidebar-item--disabled': item.isDisabled
        }"
        :data-v-step="item.vstep"
        v-show="(item.isVisible || item.isVisible === undefined) && item.iconName"
        :style="`grid-row: ${item.slot}/${item.slot+1}`"
        @click.stop="sidebarItemClickHandler(item)"
      />
    </div>

    <div class="sidebar-content" :class="{ 'sidebar-content--active': staticComponent }">
      <div class="sidebar-content-wrapper">
        <SearchBar data-v-step="6" class="sidebar-content__search" :hollow="true" v-if="shouldShowSearchBar" />
        <component class="sidebar-content__panel" :is="staticComponent" />
      </div>
    </div>
  </nav>
</template>

<script>
import NotificationsPanel from './notifications/NotificationsPanel.vue';
import LinksPanel from './links/LinksPanel.vue';
import InfoPanel from './info/InfoPanel.vue';
// it's been said that iFrame panels are currently unused, but I'm leaving the functionality in there just in case
// and I refactored a ton of code to align the static panels with them, so y'know
import IframePanel from './IframePanel.vue';
import SearchPanel from './SearchPanel.vue';
import ProfilePanel from './ProfilePanel.vue';
import SettingsPanel from './settings/SettingsPanel.vue';
import Chat from './chat/Chat.vue';

import SidebarItem from './SidebarItem.vue';

import SearchBar from '@/components/search/SearchBar.vue';

import { arrayToObject, matchAny } from '@/functions/utility.js';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'Sidebar',
  props: {
    link: String
  },
  components: { SidebarItem, IframePanel, SearchBar },
  computed: {
    ...mapGetters('sidebar', ['panelId']),
    ...mapGetters('current_user', ['isGuest', 'isExternalGuest']),
    ...mapGetters([
      'getIfCurrentUsersRoomIsHub',
      'getNewChatWaiting',
      'getCurrentAreaData',
      'getIfOnJoinedRoomPage',
      'getCurrentSpacePageData',
      'linkFrame/current',
      'sidebar/isHidden',
      'getNewNotification',
      'guest/room/currentPendingGuest',
      'getAreaIdFromLocation',
      'isOpenSpace'
    ]),
    ...mapGetters({
      isAllowed: 'permissions/isAllowed'
    }),
    shouldShowSearchBar() {
      //dont show search to room guests (when not open space)
      return this.panelId !== 'search' && (!this.isGuest || this.isOpenSpace);
    },
    isHidden: (vm) => vm['sidebar/isHidden'],
    staticComponent: (vm) => vm.staticObject[vm.panelId]?.component,
    userInSpace: (vm) => Boolean(vm.getCurrentAreaData?.id) || vm.getAreaIdFromLocation, //won't have current area if guest
    staticObject: (vm) => arrayToObject(vm.staticArray, (d) => d.id),
    staticArray: (vm) => [
      {
        id: 'home',
        name: 'My Spaces',
        isVisible:
          vm.getIfCurrentUsersRoomIsHub &&
          !matchAny(vm.$route.name, ['admin-settings', 'manage-users', 'customise', 'room', 'space-invites']) &&
          !vm['linkFrame/current'],
        iconName: 'home',
        click: () => vm['popups/nav/confirmSwitchArea'](),
        slot: 1,
        isDisabled: vm.$route.name === 'home'
      },
      {
        id: 'overview',
        name: 'Overview',
        isVisible:
          (!vm.isGuest || vm.isOpenSpace) && !vm['linkFrame/current'] && vm.$route.name !== 'manage-users' && vm.$route.name !== 'customise' &&
          //  we use "overview" as a placeholder when we don't know the conference type yet (hasnt loaded from BE)
          ((vm.$route.name === 'room' && (vm.getCurrentSpacePageData?.conferenceType == undefined || matchAny(vm.getCurrentSpacePageData.conferenceType, ['spacein', 'livestream']))) ||
            matchAny(vm.$route.name, ['admin-settings'])),
        iconName: 'return',
        click: () => vm['routing/goToCurrentArea'](),
        ...vm.disableForGuest('overview.view'),
        slot: 1
      },
      {
        id: 'exitSettings',
        name: 'Exit Settings',
        isVisible:
          matchAny(vm.$route.name, ['manage-users', 'customise']),
        iconName: 'exit',
        click: () => vm['routing/goToCurrentArea'](),
        slot: 1
      },
      {
        id: 'goToRoomLobby',
        name: 'Leave Room',
        isVisible: vm.isGuest && vm.getIfOnJoinedRoomPage && !vm.isOpenSpace,
        iconName: 'return',
        // Return to the RoomLobby, passing in the saved invite token
        click: () => vm['guest/room/returnToRoomLobby']([false]),
        slot: 1
      },
      {
        id: 'exitLink',
        name: 'Exit Content',
        isVisible:
          ((!vm.isGuest || vm.isOpenSpace) && vm.$route.name === 'room' &&
            matchAny(vm.getCurrentSpacePageData.conferenceType, ['networking', 'unknown', 'internal', 'link'])) ||
          vm['linkFrame/current'],
        iconName: 'return',
        click: () => (vm['linkFrame/current'] ? vm['linkFrame/close']() : vm.handleLeaveRoom()),
        slot: 1
      },
      {
        id: 'backTo',
        name: 'Return',
        isVisible: !vm.getIfCurrentUsersRoomIsHub && vm.$route.name == 'area' && !vm['linkFrame/current'],
        iconName: 'return',
        click: () => vm.enterCurrentUsersSpace(),
        slot: 1
      },
      {
        id: 'info',
        name: 'Info',
        isVisible: vm.userInSpace,
        iconName: 'info',
        component: InfoPanel,
        dot: vm.getIfOnJoinedRoomPage && vm.panelId !== 'info' && vm['guest/room/currentPendingGuest'] != null,
        vstep: 2,
        slot: 2
      },
      {
        id: 'chats',
        name: 'Chat',
        isVisible: vm.userInSpace,
        iconName: 'chat',
        dot: vm.getNewChatWaiting,
        component: Chat,
        vstep: 5,
        isDisabled: !vm.isAllowed('chats.view'),
        slot: 3
      },
      {
        id: 'notifications',
        name: 'Notifications',
        isVisible: vm.userInSpace,
        iconName: 'notifications',
        component: NotificationsPanel,
        dot: Boolean(vm.getNewNotification),
        vstep: 3,
        ...vm.disableForGuest('notifications.view'),
        slot: 4
      },
      {
        id: 'links',
        name: 'Content',
        isVisible: vm.userInSpace,
        iconName: 'content',
        // todo: dot
        // click: () => vm.updateAllLinksViewed(),
        component: LinksPanel,
        vstep: 4,
        ...vm.disableForGuest('content.view'),
        slot: 5
      },
      {
        id: 'settings',
        name: 'Settings',
        iconName: 'settings',
        component: SettingsPanel,
        ...vm.disableForExternalGuest('settings.view'),
        slot: 6
      },
      // these panels are implicitly hidden, because they have no icon
      {
        id: 'search',
        component: SearchPanel
      },
      {
        id: 'profile',
        component: ProfilePanel
      }
    ]
  },
  methods: {
    ...mapActions('sidebar', ['open', 'close']),
    ...mapActions([
      'popups/nav/confirmSwitchArea',
      'routing/goToCurrentArea',
      'routing/goToRoom',
      'enterCurrentUsersSpace',
      'handleLeaveRoom',
      'guest/room/returnToRoomLobby',
      'linkFrame/close'
    ]),
    sidebarItemClickHandler(item) {
      if (item.isDisabled) return;

      if (this.panelId == item.id) return this.close();

      if (this.staticObject[item.id]) {
        item.click?.call();
        if (!item.component) return;
      }

      this.open(item.id);
    },
    disableForGuest(permission) {
      if (!this.isOpenSpace && (!this.isAllowed(permission) || !this.getCurrentAreaData?.id)) {
        return {
          isDisabled: true,
          disabledMessage: 'Feature active to Space members only.'
        };
      }

      return {};
    },
    disableForExternalGuest(permission) {
      //don't show as disabled on pages where no location req = no permissions (ie: space invites page)
      if(this.$route?.meta?.requiresAuth === false) return {isDisabled: false}
      return !this.isAllowed(permission)
        ? {
            isDisabled: true,
            disabledMessage: 'Feature active to registered members only.'
          }
        : {};
    }
  },
};
</script>

<style scoped lang="scss">
@import '~@/scss/components/sidebar/sidebar.scss';
</style>
